import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatDate, formatApiError, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarDays, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import DonorQuickView from "@/components/DonorQuickView";

const FALLBACK_TYPES = [
  { value: "sunday_am", label: "Sunday AM" },
  { value: "sunday_pm", label: "Sunday PM" },
  { value: "wednesday", label: "Wednesday" },
  { value: "special", label: "Special Event" },
];

const blank = { date: todayISO(), type: "sunday_am", name: "", attendance: 0, visitors: 0, first_time_guests: 0, decisions: 0, notes: "" };

export default function ServicesLogPage() {
  const { activeOrgId } = useAuth();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [viewService, setViewService] = useState(null);
  const [viewCounts, setViewCounts] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [donorQV, setDonorQV] = useState(null);

  const openServiceCounts = async (s) => {
    setViewService(s);
    setViewCounts([]);
    setViewLoading(true);
    try {
      const [countsRes, donorsRes, catsRes] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/counts`),
        api.get(`/organizations/${activeOrgId}/donors`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/categories`).catch(() => ({ data: [] })),
      ]);
      const donorMap = {};
      (donorsRes.data || []).forEach((d) => { donorMap[d.id] = d.name; });
      const catMap = {};
      (catsRes.data || []).forEach((c) => { catMap[c.id] = c.name; });
      const filtered = (countsRes.data || []).filter((c) => c.service_id === s.id).map((c) => ({
        ...c,
        checks: (c.checks || []).map((ch) => ({ ...ch, donor_name: donorMap[ch.donor_id] || null, category_name: catMap[ch.category_id] || null })),
        cards: (c.cards || []).map((cd) => ({ ...cd, donor_name: donorMap[cd.donor_id] || null, category_name: catMap[cd.category_id] || null })),
      }));
      setViewCounts(filtered);
    } finally { setViewLoading(false); }
  };

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [svc, tps] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/services`),
        api.get(`/organizations/${activeOrgId}/service-types`).catch(() => ({ data: [] })),
      ]);
      setItems(svc.data);
      if (tps.data && tps.data.length) {
        setTypes(tps.data.map((t) => ({ value: t.key, label: t.name })));
        // ensure new form default points to first available type
        setForm((f) => f.type ? f : { ...f, type: tps.data[0].key });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      date: s.date, type: s.type, name: s.name || "",
      attendance: s.attendance || 0, visitors: s.visitors || 0,
      first_time_guests: s.first_time_guests || 0, decisions: s.decisions || 0,
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.date) return toast.error("Date is required");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attendance: Number(form.attendance) || 0,
        visitors: Number(form.visitors) || 0,
        first_time_guests: Number(form.first_time_guests) || 0,
        decisions: Number(form.decisions) || 0,
      };
      if (editing) await api.patch(`/organizations/${activeOrgId}/services/${editing.id}`, payload);
      else await api.post(`/organizations/${activeOrgId}/services`, payload);
      toast.success(editing ? "Service updated" : "Service logged");
      setOpen(false);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/services/${removeTarget.id}`);
      toast.success("Service removed");
      setRemoveTarget(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setRemoveTarget(null); }
  };

  return (
    <div className="space-y-5 fade-up">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Service log</div>
          <h1 className="font-serif text-3xl tracking-tight mt-1">Attendance & visits</h1>
        </div>
        <Button onClick={openNew} className="rounded-xl h-11" data-testid="service-new-button">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border-border shadow-none p-10 text-center">
          <CalendarDays className="w-7 h-7 mx-auto text-muted-foreground" />
          <div className="font-serif text-xl mt-3">No services logged yet</div>
          <p className="text-sm text-muted-foreground mt-1">Log Sunday services, midweek meetings, and special events.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <Card key={s.id} className="rounded-2xl border-border shadow-none p-4" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="rounded-lg">{s.type_label}</Badge>
                    {s.name && <span className="text-sm font-medium truncate">{s.name}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{formatDate(s.date)}</div>
                </div>
                <div className="flex gap-1 items-center">
                  <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {s.count_records > 0 ? (
                    <Badge variant="outline" className="rounded-lg text-[10px] gap-1 px-2" title="Counts attached — delete is locked">
                      Locked 🔒
                    </Badge>
                  ) : (
                    <Button variant="ghost" size="icon" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => setRemoveTarget(s)} data-testid={`service-delete-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <Stat label="Attendance" value={s.attendance} />
                <Stat label="Visitors" value={s.visitors} />
                <Stat label="First-time" value={s.first_time_guests} />
                <Stat label="Decisions" value={s.decisions} />
              </div>
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{s.count_records} count{s.count_records === 1 ? "" : "s"} linked</span>
                {s.count_records > 0 ? (
                  <button
                    type="button"
                    onClick={() => openServiceCounts(s)}
                    className="font-mono tabular-nums text-primary hover:underline underline-offset-4 cursor-pointer"
                    data-testid={`service-offering-${s.id}`}
                  >
                    {formatUSD(s.offering_total)} →
                  </button>
                ) : (
                  <span className="font-mono tabular-nums text-muted-foreground">{formatUSD(s.offering_total)}</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit service" : "Log a service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Date <span className="text-destructive">*</span></Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl h-11" data-testid="service-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-xl h-11" data-testid="service-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name (optional)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11" placeholder="e.g. Easter Sunrise" data-testid="service-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Attendance" value={form.attendance} onChange={(v) => setForm({ ...form, attendance: v })} testid="service-attendance" />
              <NumField label="Visitors" value={form.visitors} onChange={(v) => setForm({ ...form, visitors: v })} testid="service-visitors" />
              <NumField label="First-time guests" value={form.first_time_guests} onChange={(v) => setForm({ ...form, first_time_guests: v })} testid="service-first-time" />
              <NumField label="Decisions" value={form.decisions} onChange={(v) => setForm({ ...form, decisions: v })} testid="service-decisions" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" />
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="rounded-xl soft-glow" data-testid="service-save-button">
                {submitting ? "Saving…" : editing ? "Save" : "Log service"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title={`Delete this ${removeTarget?.type_label} service log?`}
        description="Linked count totals stay in the ledger."
        confirmLabel="Delete"
        onConfirm={doDelete}
        testid="confirm-delete-service"
      />

      <Dialog open={!!viewService} onOpenChange={(o) => { if (!o) { setViewService(null); setViewCounts([]); } }}>
        <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="service-counts-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {viewService?.type_label}{viewService?.name ? ` · ${viewService.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {viewService && (
            <div className="space-y-3 py-1">
              <div className="text-xs text-muted-foreground">{formatDate(viewService.date)} · {viewCounts.length} count{viewCounts.length === 1 ? "" : "s"}</div>
              {viewLoading ? (
                <div className="text-sm text-muted-foreground py-4">Loading counts…</div>
              ) : viewCounts.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4">No counts linked to this service yet.</div>
              ) : (
                viewCounts.map((c) => (
                  <Card key={c.id} className="rounded-2xl border-border shadow-none p-4" data-testid={`service-count-detail-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate text-sm">{c.counter_1_name} & {c.counter_2_name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {c.status === "verified" ? "Verified" : c.status === "deposited" ? "Deposited" : "Pending"}
                          {(c.signatures || []).length > 0 && ` · ${(c.signatures || []).length}/2 signed`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono tabular-nums text-primary">{formatUSD(c.total_amount)}</div>
                        {c.cards_fees > 0 && <div className="text-[10px] text-muted-foreground">net {formatUSD(c.net_amount)}</div>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      <div className="bg-secondary/40 rounded-lg py-1.5">
                        <div className="text-[9px] uppercase text-muted-foreground">Cash</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(c.cash_total)}</div>
                      </div>
                      <div className="bg-secondary/40 rounded-lg py-1.5">
                        <div className="text-[9px] uppercase text-muted-foreground">Checks</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(c.checks_total)}</div>
                      </div>
                      <div className="bg-secondary/40 rounded-lg py-1.5">
                        <div className="text-[9px] uppercase text-muted-foreground">Cards</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(c.cards_total)}</div>
                      </div>
                    </div>
                    {/* Bill counter breakdown — treasurers cross-check the physical bills against the deposit slip. */}
                    {(() => {
                      const cd = c.cash_denominations || {};
                      const denoms = [
                        { key: "hundreds", label: "$100", value: 100 },
                        { key: "fifties",  label: "$50",  value: 50 },
                        { key: "twenties", label: "$20",  value: 20 },
                        { key: "tens",     label: "$10",  value: 10 },
                        { key: "fives",    label: "$5",   value: 5 },
                        { key: "twos",     label: "$2",   value: 2 },
                        { key: "ones",     label: "$1",   value: 1 },
                      ];
                      const totalBills = denoms.reduce((s, d) => s + (Number(cd[d.key]) || 0) * d.value, 0);
                      const coins = Number(c.coins_amount) || 0;
                      const anyCount = totalBills > 0 || coins > 0;
                      if (!anyCount) return null;
                      return (
                        <div className="mt-3 rounded-xl border border-border p-2.5" data-testid={`count-bills-${c.id}`}>
                          <div className="flex items-center justify-between text-[10px] uppercase text-muted-foreground mb-1.5">
                            <span>Bill counter</span>
                            <span className="normal-case tracking-normal">Physical total <span className="font-mono tabular-nums text-foreground">{formatUSD(totalBills + coins)}</span></span>
                          </div>
                          <div className="grid grid-cols-7 gap-1.5">
                            {denoms.map((d) => {
                              const qty = Number(cd[d.key]) || 0;
                              const sub = qty * d.value;
                              return (
                                <div
                                  key={d.key}
                                  className={`rounded-lg py-1.5 text-center ${qty > 0 ? "bg-primary/5" : "bg-secondary/30 opacity-50"}`}
                                  data-testid={`count-bill-${d.key}-${c.id}`}
                                >
                                  <div className="text-[9px] uppercase text-muted-foreground">{d.label}</div>
                                  <div className="font-mono tabular-nums text-[11px] leading-tight">×{qty}</div>
                                  <div className="font-mono tabular-nums text-[10px] text-muted-foreground leading-tight">{formatUSD(sub)}</div>
                                </div>
                              );
                            })}
                          </div>
                          {coins > 0 && (
                            <div className="mt-1.5 flex justify-between text-[11px] pt-1.5 border-t border-border">
                              <span className="text-muted-foreground">Coins</span>
                              <span className="font-mono tabular-nums">{formatUSD(coins)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {(c.checks || []).length > 0 && (
                      <div className="mt-3 text-xs">
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">Cash gifts & checks</div>
                        {c.checks.map((ch, i) => {
                          const isCash = (ch.payment_type || "").toLowerCase() === "cash" || !(ch.check_number || "").trim();
                          const designation = ch.category_name || (isCash ? "General Offering" : "Tithes");
                          const note = (ch.notes || "").trim();
                          return (
                            <div key={i} className="border-t border-border py-1.5" data-testid={`count-check-row-${c.id}-${i}`}>
                              <div className="flex justify-between gap-2">
                                <span className="min-w-0 truncate">
                                  {ch.donor_name ? (
                                    ch.donor_id ? (
                                      <button
                                        className="text-foreground hover:text-primary hover:underline underline-offset-2"
                                        onClick={() => setDonorQV(ch.donor_id)}
                                        data-testid={`count-check-donor-${c.id}-${i}`}
                                      >
                                        {ch.donor_name}
                                      </button>
                                    ) : <span className="text-foreground">{ch.donor_name}</span>
                                  ) : (
                                    <span className="text-muted-foreground italic">Anonymous</span>
                                  )}
                                  <span className="text-muted-foreground"> · </span>
                                  <span className="text-primary">{designation}</span>
                                  {!isCash && (ch.check_number || "").trim() && (
                                    <span className="text-muted-foreground"> · Check #{ch.check_number}</span>
                                  )}
                                </span>
                                <span className="font-mono tabular-nums shrink-0">{formatUSD(ch.amount)}</span>
                              </div>
                              {note && (
                                <div
                                  className="mt-1 pl-2 text-[11px] text-muted-foreground italic border-l-2 border-primary/30 break-words"
                                  data-testid={`count-check-note-${c.id}-${i}`}
                                >
                                  &ldquo;{note}&rdquo;
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(c.cards || []).length > 0 && (
                      <div className="mt-2 text-xs">
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">Cards</div>
                        {c.cards.map((cd, i) => {
                          const note = (cd.notes || "").trim();
                          return (
                            <div key={i} className="border-t border-border py-1.5" data-testid={`count-card-row-${c.id}-${i}`}>
                              <div className="flex justify-between gap-2">
                                <span className="min-w-0 truncate">
                                  {cd.donor_name ? (
                                    cd.donor_id ? (
                                      <button
                                        className="text-foreground hover:text-primary hover:underline underline-offset-2"
                                        onClick={() => setDonorQV(cd.donor_id)}
                                        data-testid={`count-card-donor-${c.id}-${i}`}
                                      >
                                        {cd.donor_name}
                                      </button>
                                    ) : <span className="text-foreground">{cd.donor_name}</span>
                                  ) : <span className="text-muted-foreground italic">Anonymous</span>}
                                  <span className="text-muted-foreground"> · </span>
                                  <span className="text-primary">{cd.category_name || "Tithes"}</span>
                                  {cd.last4 && <span className="text-muted-foreground"> · &bull;&bull;&bull;&bull; {cd.last4}</span>}
                                  <span className="text-muted-foreground"> (fee {formatUSD(cd.fee)})</span>
                                </span>
                                <span className="font-mono tabular-nums shrink-0">{formatUSD(cd.amount)}</span>
                              </div>
                              {note && (
                                <div
                                  className="mt-1 pl-2 text-[11px] text-muted-foreground italic border-l-2 border-primary/30 break-words"
                                  data-testid={`count-card-note-${c.id}-${i}`}
                                >
                                  &ldquo;{note}&rdquo;
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {c.notes && <div className="mt-2 text-[11px] text-muted-foreground italic">"{c.notes}"</div>}
                  </Card>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DonorQuickView
        donorId={donorQV}
        open={!!donorQV}
        onOpenChange={(o) => { if (!o) setDonorQV(null); }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-secondary/40 rounded-xl py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono tabular-nums text-lg">{value || 0}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="number" inputMode="numeric" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl h-11 text-center tabular-nums" data-testid={testid} />
    </div>
  );
}
